import { api, type AppearanceRow } from '@setplex/tria-api'
import { createEvent, createStore, sample, createEffect } from 'effector'
import {
  Dirty,
  Empty,
  isType,
  type RowStructure,
  type RowCacheByModule,
} from './lib'
import { appearanceFeature } from '~/features/appearance'
import { aye } from '~/shared/helpers'

const $identifier = appearanceFeature.$currentIdentifier

const cache: RowCacheByModule = {}

/*
 * Dynamic and lazy loading of row data
 */

export const initModule = createEvent<{ identifier: string }>()
const restoreCached = createEvent<{
  identifier: string
  cached: RowStructure | null
}>()
export const leaveModule = createEvent<{ identifier: string }>()

export const loadRow = createEvent<AppearanceRow>()

export const $loadRowQueue = createStore<AppearanceRow[]>([])

export const loadRows = createEvent<{ identifier: string }>()
export const resetRows = createEvent()

// store with rows structure  of current module
export const $currentRows = createStore<RowStructure | null>(null)
  .on(restoreCached, (_, { cached }) => cached)
  .reset(resetRows)

// restore cached data
sample({
  clock: initModule,
  fn: ({ identifier }) => {
    const cached = cache[identifier]

    if (!cached) return { identifier, cached: null } // clear previous module data

    return { identifier, cached }
  },
  target: restoreCached,
})

const writeCacheFx = createEffect<
  { rows: RowStructure | null; identifier: string },
  void
>(({ rows, identifier }) => {
  cache[identifier] = rows
})

sample({
  clock: $currentRows,
  source: $identifier,
  filter: (identifier) => aye(identifier),
  fn: (identifier, rows) => ({ rows, identifier: identifier! }),
  target: writeCacheFx,
})

const clearCacheFx = createEffect<void, void>(() => {
  // eslint-disable-next-line guard-for-in
  for (const key in cache) delete cache[key]
})

// save module data
sample({
  clock: leaveModule,
  source: $currentRows,
  fn: (rows, { identifier }) => ({ rows, identifier }),
  target: writeCacheFx,
})

// clear modules data
sample({
  clock: resetRows,
  target: clearCacheFx,
})

// decide rows load needed after main store cache restored
sample({
  clock: restoreCached,
  // if some rows exist - then new rows load controled by "loadMoreRows"
  filter: ({ cached }) => (cached ? cached.size === 0 : true),
  target: loadRows,
})

export const $isBannersLoading = api.banners.getBannersFx.pending

// load dirty rows on the page load
sample({
  clock: loadRows,
  source: {
    currentRows: $currentRows,
    queue: $loadRowQueue,
  },
  filter: ({ currentRows }) => (currentRows ? currentRows?.size > 0 : false),
  fn: ({ currentRows, queue }) => {
    // Create a new array to avoid mutating the original queue
    const updatedQueue = [...queue]

    for (const [, { data, desc }] of currentRows!)
      if (isType(data, Dirty)) {
        updatedQueue.push(desc)
      }

    return updatedQueue
  },
  target: $loadRowQueue,
})

// add row to the load queue if it is empty or dirty
// do not reload already loaded and not refreshable rows
sample({
  clock: loadRow,
  source: { currentRows: $currentRows, queue: $loadRowQueue },
  filter: ({ currentRows }, { id }) => {
    const data = currentRows?.get(id)?.data
    return data == null || isType(data, Empty) || isType(data, Dirty)
  },
  fn: ({ queue }, row) => [...queue, row],
  target: $loadRowQueue,
})
